import type { NominationWizardDraft } from '../../../types/nominationCaseRequest';
import {
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import type { NominationWizardStep } from './nominationConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { isHighUrgencyLevel, minDeadlineDateForNomination } from './nominationHelpers';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validateNominationStep(
  step: NominationWizardStep,
  draft: NominationWizardDraft,
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'nom-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'nom-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'nom-description' },
    );

    if (draft.hasConnectedMatters === 'yes') {
      specs.push({
        key: 'connectedCaseNumber',
        label: 'Connected Case Number',
        value: draft.connectedCaseNumber,
        elementId: 'nom-connectedCaseNo',
      });
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push({ key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'nom-urgency' });
    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'nom-urgencyJustification',
      });
    }
    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 3) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'nom-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'nom-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 4) {
    Object.assign(extra, validateStep4Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep4Documents(draft: NominationWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export { isHighUrgencyLevel, minDeadlineDateForNomination };
